import React, { useState } from 'react';
import {
  PlusOutlined,
  CodeOutlined,
  LinkOutlined,
  GithubOutlined,
  CheckCircleOutlined,
  EditOutlined,
  StarOutlined,
} from '@ant-design/icons';
import {
  Form,
  Input,
  Button,
  Select,
  Checkbox,
  Upload,
  Alert,
  Card,
  Typography,
  Space,
  Divider,
  message,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  createDevProject,
  getDevProjects,
} from '../../features/projects/devprojectSlice';
import { techStackOptions } from '../../assets/data/stack';

const { TextArea } = Input;
const { Title, Text } = Typography;

const CreateDevForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.devprojects);
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleFormSubmit = async (values) => {
    try {
      console.log('=== DEV FORM SUBMIT DEBUG ===');
      console.log('Form values:', values);
      console.log('File from values:', values.file);
      console.log('=============================');

      const { name, desc, link, github, status, type, priority, stacks, file } = values;

      // Validation checks
      if (!name || !desc || !type) {
        console.error('Missing required fields');
        setFailed(true);
        return;
      }

      if (!stacks || (Array.isArray(stacks) && stacks.length === 0)) {
        console.error('No stacks selected');
        setFailed(true);
        return;
      }

      // Check if file is required
      if (!file || !file.length) {
        console.error('No file selected - this is required!');
        message.error('Please select a project image');
        setFailed(true);
        return;
      }

      const formData = new FormData();
      formData.append('name', name);
      formData.append('desc', desc);
      // Remove body field - will be added separately later
      formData.append('link', link || '');
      formData.append('github', github || '');
      formData.append('status', status.toString());
      formData.append('type', type);
      formData.append('priority', priority || '');
      formData.append(
        'stacks',
        Array.isArray(stacks) ? stacks.join(',') : stacks
      );

      // Handle file upload
      const fileObj = file[0].originFileObj || file[0];
      if (fileObj) {
        console.log('Adding file to FormData:', fileObj);
        formData.append('file', fileObj);
      } else {
        console.error('No valid file object found');
        message.error('Invalid file selected');
        setFailed(true);
        return;
      }

      console.log('=== FINAL FORMDATA ===');
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }
      console.log('=====================');

      await dispatch(createDevProject({ formData })).unwrap();
      await dispatch(getDevProjects());

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);

      form.resetFields();
    } catch (err) {
      console.error('Create project error:', err);
      setFailed(true);
      setTimeout(() => {
        setFailed(false);
      }, 3000);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Form validation failed:', errorInfo);
    setFailed(true);
    setTimeout(() => {
      setFailed(false);
    }, 3000);
  };

  // Custom file upload props
  const uploadProps = {
    name: 'file',
    multiple: false,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return Upload.LIST_IGNORE;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('Image must be smaller than 10MB!');
        return Upload.LIST_IGNORE;
      }
      return false; // Prevent auto upload
    },
    onChange(info) {
      console.log('Upload onChange:', info);
      // Update form field value when file list changes
      form.setFieldsValue({ file: info.fileList });
    },
    onRemove: (file) => {
      console.log('File removed:', file);
      // Update form when file is removed
      const newFileList =
        form.getFieldValue('file')?.filter((item) => item.uid !== file.uid) ||
        [];
      form.setFieldsValue({ file: newFileList });
    },
    customRequest: ({ file, onSuccess }) => {
      // Don't actually upload, just mark as success for form validation
      console.log('Custom request for file:', file);
      setTimeout(() => {
        onSuccess('ok');
      }, 0);
    },
  };

  

  const groupedStacks = techStackOptions.reduce((acc, stack) => {
    if (!acc[stack.category]) {
      acc[stack.category] = [];
    }
    acc[stack.category].push(stack);
    return acc;
  }, {});

  return (
    <div className='max-w-4xl mx-auto p-6 bg-bg-primary'>
      {/* Header Section */}
      <div className='mb-8 text-center'>
        <Space direction='vertical' size='small'>
          <div className='flex items-center justify-center space-x-2 mb-2'>
            <CodeOutlined className='text-2xl text-accent-blue' />
            <Title level={2} className='m-0 text-text-primary font-semibold'>
              Create Development Project
            </Title>
          </div>
          <Text className='text-text-secondary text-base'>
            Add your latest development project to showcase your skills and
            expertise
          </Text>
        </Space>
      </div>

      {/* Alert Messages */}
      {success && (
        <Alert
          className='mb-6 border border-accent-green bg-green-50 rounded-lg'
          message='Project created successfully!'
          description='Your development project has been added to your portfolio.'
          type='success'
          showIcon
          closable
          onClose={() => setSuccess(false)}
        />
      )}

      {failed && (
        <Alert
          className='mb-6 border border-accent-red bg-red-50 rounded-lg'
          message='Submission Failed'
          description='Please check all required fields and try again.'
          type='error'
          showIcon
          closable
          onClose={() => setFailed(false)}
        />
      )}

      {/* Main Form Card */}
      <Card
        className='shadow-medium border border-border-light rounded-xl bg-white'
        styles={{ body: { padding: '32px' } }}
      >
        <Form
          form={form}
          layout='vertical'
          onFinish={handleFormSubmit}
          onFinishFailed={onFinishFailed}
          size='large'
          className='space-y-6'
        >
          {/* Basic Information Section */}
          <div className='mb-8'>
            <Title
              level={4}
              className='text-text-primary mb-4 flex items-center'
            >
              <CheckCircleOutlined className='mr-2 text-accent-blue' />
              Basic Information
            </Title>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Form.Item
                label={
                  <span className='text-text-primary font-medium'>
                    Project Name
                  </span>
                }
                name='name'
                rules={[
                  { required: true, message: 'Please enter a project name' },
                ]}
                className='mb-0'
              >
                <Input
                  placeholder='Enter your project name'
                  className='h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue'
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className='text-text-primary font-medium'>
                    Project Type
                  </span>
                }
                name='type'
                rules={[
                  { required: true, message: 'Please select a project type' },
                ]}
                className='mb-0'
              >
                <Select
                  placeholder='Select project type'
                  className='h-12'
                  popupClassName='border border-border-light rounded-lg'
                >
                  <Select.Option value='web'>🌐 Web Application</Select.Option>
                  <Select.Option value='mobile'>
                    📱 Mobile Application
                  </Select.Option>
                  <Select.Option value='desktop'>
                    🖥️ Desktop Application
                  </Select.Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              label={
                <span className='text-text-primary font-medium'>
                  Description
                </span>
              }
              name='desc'
              rules={[
                { required: true, message: 'Please enter a description' },
              ]}
              className='mt-6'
            >
              <TextArea
                rows={4}
                placeholder='Describe your project, its features, and what makes it unique...'
                className='border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue resize-none'
              />
            </Form.Item>
          </div>
          <Divider className='border-border-light' />
          
          {/* Priority Section */}
          <div className='mb-8'>
            <Title
              level={4}
              className='text-text-primary mb-4 flex items-center'
            >
              <StarOutlined className='mr-2 text-accent-blue' />
              Project Priority
            </Title>

            <Form.Item
              label={
                <span className='text-text-primary font-medium'>
                  Priority Level
                </span>
              }
              name='priority'
              className='mb-0'
            >
              <Select
                placeholder='Select priority (optional)'
                className='h-12 max-w-xs'
                allowClear
                popupClassName='border border-border-light rounded-lg'
              >
                <Select.Option value='featured'>⭐ Featured Project</Select.Option>
                <Select.Option value='other'>📂 Other Project</Select.Option>
              </Select>
            </Form.Item>
            
            <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg mt-4'>
              <Text className='text-blue-700 text-sm'>
                <strong>💡 Priority Info:</strong> Only one project can be marked as "Featured" at a time. 
                Featured projects get special highlighting in your portfolio.
              </Text>
            </div>
          </div>
          <Divider className='border-border-light' />
          
          {/* Links Section */}
          <div className='mb-8'>
            <Title
              level={4}
              className='text-text-primary mb-4 flex items-center'
            >
              <LinkOutlined className='mr-2 text-accent-blue' />
              Project Links
            </Title>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Form.Item
                label={
                  <span className='text-text-primary font-medium'>
                    Live Demo URL
                  </span>
                }
                name='link'
                className='mb-0'
              >
                <Input
                  placeholder='https://your-project.com'
                  prefix={<LinkOutlined className='text-neutral-400' />}
                  className='h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue'
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className='text-text-primary font-medium'>
                    GitHub Repository
                  </span>
                }
                name='github'
                className='mb-0'
              >
                <Input
                  placeholder='https://github.com/username/repo'
                  prefix={<GithubOutlined className='text-neutral-400' />}
                  className='h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue'
                />
              </Form.Item>
            </div>

            <Form.Item
              label={
                <span className='text-text-primary font-medium'>
                  Project Status
                </span>
              }
              name='status'
              rules={[
                { required: true, message: 'Please select project status' },
              ]}
              className='mt-6'
            >
              <Select
                placeholder='Select project status'
                className='h-12 max-w-xs'
              >
                <Select.Option value='false'>🚧 In Development</Select.Option>
                <Select.Option value='true'>✅ Completed</Select.Option>
              </Select>
            </Form.Item>
          </div>
          <Divider className='border-border-light' />
  
          {/* Technology Stack Section */}
          <div className='mb-8'>
            <Title
              level={4}
              className='text-text-primary mb-4 flex items-center'
            >
              <CodeOutlined className='mr-2 text-accent-blue' />
              Technology Stack
            </Title>

            <Form.Item
              name='stacks'
              rules={[
                {
                  required: true,
                  message: 'Please select at least one technology',
                },
              ]}
              className='mb-0'
            >
              <Checkbox.Group className='w-full'>
                <div className='space-y-6'>
                  {Object.entries(groupedStacks).map(([category, stacks]) => (
                    <div
                      key={category}
                      className='bg-bg-secondary p-4 rounded-lg border border-border-light'
                    >
                      <Text className='text-text-primary font-semibold text-sm uppercase tracking-wide mb-3 block'>
                        {category}
                      </Text>
                      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3'>
                        {stacks.map((stack) => (
                          <Checkbox
                            key={stack.value}
                            value={stack.value}
                            className='text-text-secondary hover:text-text-primary transition-colors duration-200'
                          >
                            <span className='text-sm'>{stack.label}</span>
                          </Checkbox>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Additional Tools Section - Add this to match design form */}
                  <div className='bg-neutral-50 p-4 rounded-lg border border-border-light'>
                    <Text className='text-text-secondary text-xs mb-2 block'>
                      💡 <strong>Pro Tip:</strong> Select the technologies you
                      actively used in this project. This helps potential
                      employers and clients understand your technical expertise
                      and project complexity.
                    </Text>
                  </div>
                </div>
              </Checkbox.Group>
            </Form.Item>
          </div>
          <Divider className='border-border-light' />
          {/* File Upload Section */}
          <div className='mb-8'>
            <Title
              level={4}
              className='text-text-primary mb-4 flex items-center'
            >
              <PlusOutlined className='mr-2 text-accent-blue' />
              Project Image
            </Title>

            <Form.Item
              name='file'
              valuePropName='fileList'
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e && e.fileList;
              }}
              rules={[
                {
                  required: true,
                  message: 'Please upload a project image',
                },
                {
                  validator: (_, fileList) => {
                    if (!fileList || fileList.length === 0) {
                      return Promise.reject(
                        new Error('Please upload a project image')
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              className='mb-4'
            >
              <div className='border-2 border-dashed border-border-light rounded-xl bg-bg-secondary hover:border-accent-blue hover:bg-accent-blue/5 transition-all duration-300 overflow-hidden'>
                <Upload.Dragger
                  {...uploadProps}
                  maxCount={1}
                  accept='image/*'
                  className='!border-0 !bg-transparent'
                  style={{
                    padding: '32px 24px',
                    background: 'transparent',
                  }}
                >
                  <div className='text-center'>
                    <CodeOutlined className='text-4xl text-accent-blue mb-4' />
                    <div className='space-y-2 mb-4'>
                      <Text className='text-text-primary text-base font-medium block'>
                        Click to upload project image
                      </Text>
                      <Text className='text-text-secondary text-sm block'>
                        or drag and drop your screenshot here
                      </Text>
                    </div>
                    <div className='text-xs text-neutral-400 space-y-1'>
                      <div>PNG, JPG up to 10MB</div>
                      <div>
                        Recommended: App screenshots or website previews
                      </div>
                    </div>
                  </div>
                </Upload.Dragger>
              </div>
            </Form.Item>

            <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
              <Text className='text-text-secondary text-sm'>
                <strong>📸 Upload Tips:</strong> Use screenshots of your live
                application, code snippets, or project architecture diagrams to
                showcase your development work effectively.
              </Text>
            </div>
          </div>
          {/* Submit Section */}
          <div className='pt-6 border-t border-border-light'>
            <div className='flex justify-center'>
              <Button
                type='primary'
                htmlType='submit'
                loading={loading}
                size='large'
                className='h-12 px-8 bg-accent-blue hover:bg-accent-blue/90 border-accent-blue hover:border-accent-blue/90 rounded-lg font-semibold shadow-soft hover:shadow-medium transition-all duration-200'
              >
                {loading ? 'Creating Project...' : 'Create Project'}
              </Button>
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CreateDevForm;